/**
 * viem LocalAccount adapter for 1auth passkey-controlled smart accounts.
 *
 * Bridges viem's `LocalAccount` interface to the 1auth passkey service so that
 * any viem-based library (e.g. permissionless, viem itself) can use a passkey
 * account without handling WebAuthn directly. All cryptographic operations are
 * delegated to the passkey service via `OneAuthClient`.
 *
 * @module
 */

import {
  bytesToString,
  hexToString,
  isHex,
  type Address,
  type LocalAccount,
  type SignableMessage,
  type TypedData,
  type TypedDataDefinition,
} from "viem";
import { toAccount } from "viem/accounts";
import { OneAuthClient } from "./client";
import type { EIP712Domain, EIP712Types } from "./types";
import { toOneAuthError } from "./errors";
import { encodeWebAuthnSignature } from "./walletClient/utils";

export type PasskeyAccount = LocalAccount<"1auth"> & {
  /** Smart account address used as the canonical signer identity. */
  accountAddress: Address;
};

/**
 * Creates a viem `LocalAccount` that delegates signing to the 1auth passkey service.
 *
 * The returned account satisfies the `LocalAccount` interface expected by viem's
 * `createWalletClient` and similar APIs. `signTransaction` is explicitly unsupported
 * because 1auth uses the intent/bundler model instead of raw signed transactions —
 * callers should use `sendIntent` (via `eth_sendTransaction` on the provider) instead.
 *
 * All signing operations encode the resulting WebAuthn signature with
 * `encodeWebAuthnSignature` so the output is directly usable with ERC-1271
 * on-chain verification.
 *
 * @param client - A configured `OneAuthClient` instance
 * @param params.address - The EVM address of the user's smart account
 * @returns A `PasskeyAccount` extending `LocalAccount<"1auth">`
 *
 * @example
 * ```typescript
 * import { createWalletClient, http } from "viem";
 * import { base } from "viem/chains";
 * import { OneAuthClient, createPasskeyAccount } from "@rhinestone/1auth";
 *
 * const client = new OneAuthClient({ clientId: "my-app" });
 * const account = createPasskeyAccount(client, {
 *   address: "0xYourSmartAccountAddress",
 * });
 *
 * const walletClient = createWalletClient({
 *   account,
 *   chain: base,
 *   transport: http(),
 * });
 *
 * const signature = await walletClient.signMessage({ message: "Hello" });
 * ```
 */
export function createPasskeyAccount(
  client: OneAuthClient,
  params: { address: Address }
): PasskeyAccount {
  const { address } = params;

  /**
   * Converts viem's polymorphic `SignableMessage` type to a plain string.
   *
   * viem allows messages to be passed as a plain string, a hex-encoded string under
   * the `{ raw: Hex }` form, or raw bytes under the `{ raw: Uint8Array }` form. The
   * passkey service only accepts plain strings, so this helper normalises all three
   * variants. Hex values are decoded to their UTF-8 representation; if decoding fails
   * (e.g. the hex is not valid UTF-8) the raw hex string is passed through unchanged.
   *
   * @param message - viem `SignableMessage` in any of its supported forms
   * @returns Plain string representation of the message
   */
  const normalizeMessage = (message: SignableMessage): string => {
    if (typeof message === "string") return message;
    const raw = message.raw;
    if (isHex(raw)) {
      try {
        return hexToString(raw);
      } catch {
        return raw;
      }
    }
    return bytesToString(raw);
  };

  const account = toAccount({
    address,
    signMessage: async ({ message }: { message: SignableMessage }) => {
      const result = await client.signMessage({
        accountAddress: address,
        message: normalizeMessage(message),
      });
      if (!result.success) {
        throw toOneAuthError(result.error, "Signing failed", "SIGNING_FAILED");
      }
      if (!result.signature) {
        throw toOneAuthError(undefined, "No signature received", "SIGNING_FAILED");
      }
      return encodeWebAuthnSignature(result.signature);
    },
    signTransaction: async () => {
      throw new Error("signTransaction not supported; use sendIntent");
    },
    signTypedData: async <
      const typedData extends TypedData | Record<string, unknown>,
      primaryType extends keyof typedData | "EIP712Domain" = keyof typedData
    >(
      typedData: TypedDataDefinition<typedData, primaryType>
    ) => {
      if (!typedData.domain || !typedData.types || !typedData.primaryType) {
        throw new Error("Invalid typed data");
      }
      const domainInput = typedData.domain as Partial<EIP712Domain>;
      if (!domainInput.name || !domainInput.version) {
        throw new Error("Typed data domain must include name and version");
      }
      const domain: EIP712Domain = {
        name: domainInput.name,
        version: domainInput.version,
        // viem uses bigint for chainId in typed data; the passkey service expects number
        chainId:
          typeof domainInput.chainId === "bigint"
            ? Number(domainInput.chainId)
            : domainInput.chainId,
        verifyingContract: domainInput.verifyingContract,
        salt: domainInput.salt,
      };
      const rawTypes =
        typedData.types as Record<string, readonly { name: string; type: string }[]>;
      // Strip any extra fields (e.g. readonly markers) that viem attaches to type
      // entries — the passkey service expects plain { name, type } objects.
      const normalizedTypes = Object.fromEntries(
        Object.entries(rawTypes).map(([key, fields]) => [
          key,
          fields.map((field) => ({ name: field.name, type: field.type })),
        ])
      ) as EIP712Types;
      const result = await client.signTypedData({
        accountAddress: address,
        domain,
        types: normalizedTypes,
        primaryType: typedData.primaryType as string,
        message: typedData.message as Record<string, unknown>,
      });
      if (!result.success) {
        throw toOneAuthError(result.error, "Signing failed", "SIGNING_FAILED");
      }
      if (!result.signature) {
        throw toOneAuthError(undefined, "No signature received", "SIGNING_FAILED");
      }
      return encodeWebAuthnSignature(result.signature);
    },
  });

  return {
    ...(account as LocalAccount<"1auth">),
    accountAddress: address,
  };
}
